//framework
import React, { memo, useState, useCallback, useEffect } from 'react';

//componets
import QRCode from 'qrcode.react';
import OutlineText from 'components/Shared/OutlineText/OutlineText';

//services
import { audio } from 'services/audioManager';

//assets
import { IMAGES } from 'constants/constants';

//stlyes
import styles from './RoomDetails.module.scss';

const fullscreenAvailable =
    typeof document !== "undefined" &&
        (document.fullscreenEnabled || document.mozFullscreenEnabled || document.webkitFullscreenEnabled || document.msFullscreenEnabled) ? true : false;

function isFullScreen() {
    const d = document;
    return !!(d.fullscreenElement || d.webkitFullscreenElement || d.mozFullScreenElement || d.msFullscreenElement);
}

function toggleFullScreen() {
    if (!fullscreenAvailable) return;
    const d = document;
    const root = d.documentElement;

    if (!isFullScreen()) {
        (root.requestFullscreen || root.webkitRequestFullscreen || root.mozRequestFullScreen || root.msRequestFullscreen)?.call(root);
    } else {
        (d.exitFullscreen || d.webkitExitFullscreen || d.mozExitFullscreen || d.msExitFullscreen)?.call(d);
    }
}

const RoomDetails = memo(function RoomDetails({ url, displayUrl, mutedMusic, mutedSfx, roomId, onToggleMute, onToggleMenu, hideButtons, isMirror }) {
    const [isFs, setIsFs] = useState(false);
    // Mirror-only override; lets a mirror viewer silence their tab without affecting
    // the host or other mirrors. OR'd with the host's schema mute below.
    const [localOverride, setLocalOverride] = useState(false);

    useEffect(() => {
        const handler = () => setIsFs(isFullScreen());
        document.addEventListener('fullscreenchange', handler);
        document.addEventListener('webkitfullscreenchange', handler);
        return () => {
            document.removeEventListener('fullscreenchange', handler);
            document.removeEventListener('webkitfullscreenchange', handler);
        };
    }, []);

    // Effective mute = host's schema mute || (mirror only) local override.
    // Primary client just follows the host schema (localOverride is unused).
    const effMusic = !!mutedMusic || (isMirror && localOverride);
    const effSfx = !!mutedSfx || (isMirror && localOverride);

    // Apply effective mute to Howler. RoomDetails is mounted on both primary and
    // mirror, so this replaces the old OptionsMenu useEffects that only ran on the
    // primary host. Per-track volume → 0 is stable across tab focus / unlock events.
    useEffect(() => {
        audio.muteMusic(effMusic);
    }, [effMusic]);

    useEffect(() => {
        audio.muteSfx(effSfx);
        audio.muteVo(effSfx);
    }, [effSfx]);

    const handleMuteClick = useCallback(() => {
        if (isMirror) {
            setLocalOverride(v => !v);
        } else {
            onToggleMute();
        }
    }, [isMirror, onToggleMute]);

    // Icon reflects what's actually happening to audio on this client.
    const isMuted = effMusic && effSfx;

    return (
        <div className={styles.roomCode}>
            <OutlineText parentStyles={styles} className={styles.text} text={displayUrl || window.location.hostname} />
            <div className={styles.topRow}>
                <div className={styles.qrCodeBox}>
                    <QRCode
                        className={styles.qrCode}
                        value={`${url}/play/?qrCode=${roomId}`}
                        bgColor="rgba(0,0,0,0)"
                    />
                </div>
                {
                    !hideButtons &&
                    <div className={styles.iconsBox}>
                        <div className={styles.iconButton} onClick={handleMuteClick}>
                            <img src={isMuted ? IMAGES.SHARED.mutedIcon : IMAGES.SHARED.unmutedIcon} className={styles.icon} alt="mute" />
                        </div>
                        {
                            fullscreenAvailable &&
                            <div className={styles.iconButton} onClick={() => toggleFullScreen()}>
                                <img src={IMAGES.SHARED.fullscreenIcon} className={styles.icon} alt={isFs ? "exit fullscreen" : "fullscreen"} />
                            </div>
                        }
                        {
                            !isMirror &&
                            <div className={styles.iconButton} onClick={() => onToggleMenu()}>
                                <img src={IMAGES.SHARED.menuIcon} className={styles.icon} alt="help" />
                            </div>
                        }
                    </div>
                }
            </div>
            <div className={styles.codeRow}>
                <span className={styles.codeLabel}>CODE</span>
                <span className={styles.codeValue}>{roomId}</span>
            </div>
            {isMirror && <div className={styles.mirrorBadge}>CONNECT</div>}
        </div>
    )
});

export default RoomDetails;
